import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { eventService } from '../../services/eventService';
import { isValidMetaPixelId, sanitizeMetaPixelId } from '../../features/meta-pixel';
import MetaIntegrationNotice from './MetaIntegrationNotice';

type Props = {
  eventId: string;
  eventTitle?: string;
  onIntegrationChange?: (status: {
    pixelId: string;
    enabled: boolean;
    capiTokenConfigured: boolean;
  }) => void;
};

const EventWorkspaceMarketingTab: React.FC<Props> = ({ eventId, eventTitle, onIntegrationChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pixelId, setPixelId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [capiToken, setCapiToken] = useState('');
  const [capiTokenConfigured, setCapiTokenConfigured] = useState(false);
  const [clearCapiToken, setClearCapiToken] = useState(false);
  const onIntegrationChangeRef = useRef(onIntegrationChange);
  onIntegrationChangeRef.current = onIntegrationChange;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await eventService.getEventWorkspace(eventId);
        const data = res?.data ?? res;
        if (cancelled) return;
        setPixelId(String(data?.metaPixel?.pixelId || ''));
        setEnabled(!!data?.metaPixel?.enabled);
        setCapiTokenConfigured(!!data?.metaPixel?.capiTokenConfigured);
        setCapiToken('');
        setClearCapiToken(false);
        onIntegrationChangeRef.current?.({
          pixelId: String(data?.metaPixel?.pixelId || ''),
          enabled: !!data?.metaPixel?.enabled,
          capiTokenConfigured: !!data?.metaPixel?.capiTokenConfigured,
        });
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Erro ao carregar configuração.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleSave = async () => {
    const id = sanitizeMetaPixelId(pixelId);
    if (enabled && !isValidMetaPixelId(id)) {
      setError('Informe um Pixel ID válido (10–20 dígitos) para ativar.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await eventService.updateEventFields(eventId, {
        metaPixel: {
          pixelId: id,
          enabled: enabled && isValidMetaPixelId(id),
          inheritFromParish: false,
          ...(capiToken.trim() ? { capiAccessToken: capiToken.trim() } : {}),
          ...(clearCapiToken ? { clearCapiAccessToken: true } : {}),
        },
      });
      const savedPixel = (saved?.data as { metaPixel?: { capiTokenConfigured?: boolean } } | undefined)?.metaPixel;
      setPixelId(id);
      setCapiToken('');
      setClearCapiToken(false);
      const tokenNow =
        typeof savedPixel?.capiTokenConfigured === 'boolean'
          ? savedPixel.capiTokenConfigured
          : clearCapiToken
            ? false
            : capiToken.trim()
              ? true
              : capiTokenConfigured;
      setCapiTokenConfigured(tokenNow);
      onIntegrationChange?.({
        pixelId: id,
        enabled: enabled && isValidMetaPixelId(id),
        capiTokenConfigured: tokenNow,
      });
      setSuccess('Configuração de marketing salva.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} maxWidth={560}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CampaignIcon color="primary" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Marketing — Meta Pixel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {eventTitle ? `Evento: ${eventTitle}` : 'Configure o pixel deste evento'}
          </Typography>
        </Box>
      </Stack>

      <MetaIntegrationNotice
        pixelId={pixelId}
        enabled={enabled}
        capiTokenConfigured={capiTokenConfigured && !clearCapiToken}
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        label="Pixel ID da Meta"
        placeholder="123456789012345"
        value={pixelId}
        onChange={(e) => setPixelId(e.target.value.replace(/\D/g, ''))}
        fullWidth
        helperText="Somente números. Encontre em Meta Events Manager → Fontes de dados."
      />

      <TextField
        label="Token da API de Conversões"
        type="password"
        value={capiToken}
        onChange={(e) => {
          setCapiToken(e.target.value);
          if (e.target.value.trim()) setClearCapiToken(false);
        }}
        fullWidth
        autoComplete="new-password"
        placeholder={capiTokenConfigured ? 'Token já salvo' : 'Cole o token gerado na Meta'}
        helperText={
          capiTokenConfigured
            ? 'Já existe um token neste evento. Cole outro só para substituir. Deixe em branco para manter.'
            : 'No Events Manager do pixel: Configurações → API de Conversões → Gerar token de acesso.'
        }
        disabled={clearCapiToken}
      />

      {capiTokenConfigured ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={clearCapiToken}
              onChange={(_, checked) => {
                setClearCapiToken(checked);
                if (checked) setCapiToken('');
              }}
            />
          }
          label="Remover o token salvo"
        />
      ) : null}

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(_, v) => setEnabled(v)}
            disabled={!isValidMetaPixelId(sanitizeMetaPixelId(pixelId)) && !enabled}
          />
        }
        label="Ativar rastreamento neste evento"
      />

      <Typography variant="body2" color="text.secondary">
        Ajuda:{' '}
        <Link
          href="https://www.facebook.com/business/help/952192354843755"
          target="_blank"
          rel="noreferrer"
        >
          Como criar um pixel no Meta Events Manager
        </Link>
      </Typography>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Salvando…' : 'Salvar'}
      </Button>
    </Stack>
  );
};

export default EventWorkspaceMarketingTab;
